import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { SessionChangeHistoryPage } from './types';

export function sessionHistoryQueryKey(sessionId: string, columnId?: string) {
  return columnId
    ? (['sessionHistory', sessionId, columnId] as const)
    : (['sessionHistory', sessionId] as const);
}

export function useSessionChangeHistory(
  sessionId: string,
  opts: { columnId?: string; enabled?: boolean }
) {
  return useQuery({
    queryKey: sessionHistoryQueryKey(sessionId, opts.columnId),
    queryFn: () => api.sessions.getChanges(sessionId, { columnId: opts.columnId }),
    enabled: (opts.enabled ?? true) && !!sessionId,
    staleTime: 30_000,
  });
}

export function invalidateSessionHistory(
  queryClient: ReturnType<typeof useQueryClient>,
  sessionId: string,
  columnId?: string
) {
  queryClient.invalidateQueries({ queryKey: ['sessionHistory', sessionId] });
  if (columnId) {
    queryClient.invalidateQueries({ queryKey: sessionHistoryQueryKey(sessionId, columnId) });
  }
}

export function mergeHistoryPage(
  prev: SessionChangeHistoryPage | undefined,
  next: SessionChangeHistoryPage
): SessionChangeHistoryPage {
  if (!prev) return next;
  const seen = new Set(prev.items.map((i) => i.id));
  const merged = [...prev.items];
  for (const item of next.items) {
    if (!seen.has(item.id)) merged.push(item);
  }
  return { items: merged, next_cursor: next.next_cursor };
}
